import { defineStore } from "pinia";
import { FLYOUT_DEFAULT_WIDTH } from "@studio/Components/Frame/studio-dce-chrome-constants";
import {
  STICKY_BREAKPOINTS,
  STUDIO_BREAKPOINT_DESKTOP,
  STUDIO_BREAKPOINT_PHONE,
  isStudioBreakpoint,
  stickySlotFor,
  studioBreakpointFor,
} from "@studio/Helpers/studio-breakpoints";

export const LEGACY_CHROME_COLLAPSED_KEY = "dce:chrome-collapsed";
const SIDEBAR_COLLAPSED_KEY = "dce:sidebar-collapsed";

export function chromeCollapsedKey(breakpoint) {
  return `${LEGACY_CHROME_COLLAPSED_KEY}:${breakpoint}`;
}

function readStoredValue(key) {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}
function readPersistedFlag(key) {
  return readStoredValue(key) === "1";
}
function writePersistedFlag(key, value) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(key, value ? "1" : "0");
  } catch (error) {
    return;
  }
}
function removeStoredValue(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    return;
  }
}

export function readChromeCollapsedByBreakpoint() {
  const desktopKey = chromeCollapsedKey(STUDIO_BREAKPOINT_DESKTOP);
  const legacy = readStoredValue(LEGACY_CHROME_COLLAPSED_KEY);
  if (legacy !== null) {
    if (readStoredValue(desktopKey) === null) {
      writePersistedFlag(desktopKey, legacy === "1");
    }
    removeStoredValue(LEGACY_CHROME_COLLAPSED_KEY);
  }
  return Object.fromEntries(STICKY_BREAKPOINTS.map(breakpoint => [breakpoint, readPersistedFlag(chromeCollapsedKey(breakpoint))]));
}

function initialBreakpoint() {
  return typeof window === "undefined" ? STUDIO_BREAKPOINT_DESKTOP : studioBreakpointFor(window.innerWidth);
}

export const useStudioMobileStore = defineStore("studio-mobile-store", {
  state: () => ({
    breakpoint: initialBreakpoint(),
    chromeCollapsedByBreakpoint: readChromeCollapsedByBreakpoint(),
    phoneDrawerOpen: false,
    sidebarCollapsed: readPersistedFlag(SIDEBAR_COLLAPSED_KEY),
    railOverflowItems: [],
    railMoreOpen: false,
    railPeekSection: null,
    railHovered: false,
    railPeekDrillSection: null,
    railPeekDrillProbe: null,
    stickyDrillPeeled: false,
    flyoutExpanded: false,
    flyoutWidth: FLYOUT_DEFAULT_WIDTH,
    flyoutResizing: false,
  }),
  getters: {
    isPhone() {
      return this.breakpoint === STUDIO_BREAKPOINT_PHONE;
    },
    chromeCollapsed() {
      if (this.breakpoint === STUDIO_BREAKPOINT_PHONE) {
        return !this.phoneDrawerOpen;
      }
      return this.chromeCollapsedByBreakpoint[stickySlotFor(this.breakpoint)];
    },
  },
  actions: {
    setBreakpoint(breakpoint) {
      if (!isStudioBreakpoint(breakpoint)) {
        throw new Error(`setBreakpoint got "${breakpoint}", which is not a Studio breakpoint.`);
      }
      if (breakpoint === this.breakpoint) {
        return;
      }
      if (breakpoint === STUDIO_BREAKPOINT_PHONE) {
        this.clearRailPeek();
      }
      this.phoneDrawerOpen = false;
      this.breakpoint = breakpoint;
    },
    toggleChrome() {
      if (this.breakpoint === STUDIO_BREAKPOINT_PHONE) {
        this.setPhoneDrawerOpen(!this.phoneDrawerOpen);
        return;
      }
      this.setChromeCollapsed(!this.chromeCollapsed);
    },
    setPhoneDrawerOpen(open) {
      if (this.breakpoint !== STUDIO_BREAKPOINT_PHONE) {
        throw new Error(`The phone drawer exists only at the phone breakpoint; Studio is at "${this.breakpoint}".`);
      }
      if (!open) {
        this.clearRailPeek();
      }
      this.phoneDrawerOpen = Boolean(open);
    },
    applyFollowedChromeCollapsed(collapsed) {
      if (this.breakpoint === STUDIO_BREAKPOINT_PHONE) {
        return;
      }
      this.setChromeCollapsed(collapsed);
    },
    setRailOverflowItems(items) {
      this.railOverflowItems = items;
    },
    setRailMoreOpen(open) {
      this.railMoreOpen = open;
    },
    setRailPeek(sectionKey) {
      if (sectionKey === this.railPeekSection) {
        return;
      }
      this.railPeekSection = sectionKey;
      this.railPeekDrillSection = null;
      this.railPeekDrillProbe = null;
    },
    openSectionDrill(sectionKey, drillKey) {
      this.railPeekSection = sectionKey;
      this.railPeekDrillSection = sectionKey;
      this.railPeekDrillProbe = typeof drillKey === "string" ? { subPage: drillKey } : drillKey;
      this.stickyDrillPeeled = false;
    },
    closeDrillPane() {
      this.stickyDrillPeeled = true;
      this.railPeekDrillSection = null;
      this.railPeekDrillProbe = null;
    },
    clearStickyDrillPeel() {
      this.stickyDrillPeeled = false;
    },
    setRailHovered(hovered) {
      this.railHovered = Boolean(hovered);
    },
    clearRailPeek() {
      this.railPeekSection = null;
      this.railHovered = false;
      this.railPeekDrillSection = null;
      this.railPeekDrillProbe = null;
      this.railMoreOpen = false;
      this.stickyDrillPeeled = false;
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      writePersistedFlag(SIDEBAR_COLLAPSED_KEY, this.sidebarCollapsed);
    },
    setChromeCollapsed(collapsed) {
      if (this.breakpoint === STUDIO_BREAKPOINT_PHONE) {
        throw new Error("The phone layout has no collapse setting; its rail opens through the drawer (setPhoneDrawerOpen).");
      }
      const slot = stickySlotFor(this.breakpoint);
      this.chromeCollapsedByBreakpoint = { ...this.chromeCollapsedByBreakpoint, [slot]: Boolean(collapsed) };
      writePersistedFlag(chromeCollapsedKey(slot), Boolean(collapsed));
    },
    setSidebarCollapsed(collapsed) {
      this.sidebarCollapsed = collapsed;
      writePersistedFlag(SIDEBAR_COLLAPSED_KEY, collapsed);
    },
    setRailMenuState(menu) {
      this.railPeekSection = menu.railPeekSection;
      this.railHovered = menu.railHovered;
      this.railPeekDrillSection = menu.railPeekDrillSection;
      this.railPeekDrillProbe = menu.railPeekDrillProbe;
      this.railMoreOpen = menu.railMoreOpen;
      this.stickyDrillPeeled = menu.stickyDrillPeeled;
    },
  },
});
